import React, { Component } from 'react';
import { ICancelPendingTransactionsModalProps } from '../../types/propTypes';
import { ICancelPendingTransactionsModalState } from '../../types/stateTypes';
import { Button, Modal } from 'react-bootstrap';
import Table from '../../components/table/Table';
import moment from 'moment';
import { regionDateAndTime } from '../../utils';
import DriverService from '../../services/driver/driver-service';
import { getFormattedCurrencyWithSymbol } from '../../components/common/LocalNumberFormat';
import { store } from '../../store';
import { Common } from '../../utils/constants';
import { toast } from 'react-toastify';
import StandardResponse from '../../models/standard-response';
import { IDriverPendingTransactions, IDriverPendingTransactionsList } from '../../models/driver';
import { ConfirmBox } from '../../components/confirm-box/ConfirmBox';

class CancelPendingTransactionsModal extends Component<
    ICancelPendingTransactionsModalProps,
    ICancelPendingTransactionsModalState
> {
    private driverService: DriverService;

    constructor(props: ICancelPendingTransactionsModalProps) {
        super(props);
        this.state = {
            data: [],
            filterState: {
                rowsPerPage: 10,
                currentPage: 1,
                sortColumn: 'created_date',
                sortOrder: 'desc',
                offset: 0,
            },
            modal: {
                showModal: false,
                header: '',
                body: '',
                callBack: null,
            },
            totalCount: 0,
            checkedRecord: [],
        };
        this.driverService = new DriverService();
    }

    componentDidMount() {
        this.getPendingTransactionsData();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.filterState !== this.state.filterState) {
            this.getPendingTransactionsData();
        }
    }

    getPendingTransactionsData = async () => {
        const { id } = this.props;
        const { filterState } = this.state;
        const filterInput = {
            limit: filterState.rowsPerPage,
            offset: filterState.offset,
            order: [[filterState.sortColumn, filterState.sortOrder]],
        };

        await this.driverService
            .getDriverPendingTransactions(id, filterInput)
            .then(async (res: StandardResponse<IDriverPendingTransactionsList>) => {
                this.setState({ data: res.Data.rows, totalCount: res.Data.count });
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
    };

    settlePendingTransactions = () => {
        const { id, cancelPendingTransactions } = this.props;
        const { checkedRecord } = this.state;
        cancelPendingTransactions(id, checkedRecord);
    };

    handlePageChange = (currentPage: number, rowsPerPage: number) => {
        const offset = (currentPage - 1) * rowsPerPage;
        this.setState((prevState) => ({
            filterState: {
                ...prevState.filterState,
                currentPage,
                rowsPerPage,
                offset,
            },
        }));
    };

    handleSorting = (columnName: string) => {
        this.setState((prevState) => {
            let sortOrder = prevState.filterState.sortOrder;
            if (prevState.filterState.sortColumn === columnName) {
                sortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
            } else {
                sortOrder = 'asc';
            }
            return {
                filterState: {
                    ...prevState.filterState,
                    sortColumn: columnName,
                    sortOrder,
                },
            };
        });
    };

    handleModal = (body: string, header: string, callBack: Function | null) => {
        this.setState({
            modal: {
                body: body,
                header: header,
                callBack: callBack,
                showModal: !this.state.modal.showModal,
            },
        });
    };

    render() {
        const { showModal, closeModal, t } = this.props;
        const { data, filterState, totalCount, checkedRecord } = this.state;

        const header = [
            {
                label: t('driver.pendingTransaction.label.chargeLocation'),
                id: 'charge_location',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverPendingTransactions) => (
                    <span className="d-block text-nowrap-normal" title={row.charge_location}>
                        {row.charge_location || '-'}
                    </span>
                ),
            },
            {
                label: t('driver.pendingTransaction.label.chargeStation'),
                id: 'charge_station',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverPendingTransactions) => (
                    <span className="d-block text-nowrap-normal" title={row.charge_station}>
                        {row.charge_station || '-'}
                    </span>
                ),
            },
            {
                label: t('driver.pendingTransaction.label.sessionId'),
                id: 'session_id',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverPendingTransactions) => (
                    <span className="d-block text-nowrap-normal" title={row.session_id}>
                        {row.session_id || '-'}
                    </span>
                ),
            },
            {
                label: t('driver.pendingTransaction.label.sessionStatus'),
                id: 'status_code',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverPendingTransactions) => {
                    if (row.session_status) {
                        return (
                            <span className="badge badge-info" style={{ background: row.session_status.color }}>
                                {row.session_status.name}
                            </span>
                        );
                    }
                    return '-';
                },
            },
            {
                label: t('driver.pendingTransaction.label.amount'),
                id: 'total_authorized_amount',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverPendingTransactions) => {
                    if (row.total_authorized_amount) {
                        return (
                            <span>
                                {getFormattedCurrencyWithSymbol(
                                    row.total_authorized_amount?.toString(),
                                    'text',
                                    undefined,
                                    store.getState()?.configuration?.default_currency?.symbol,
                                )}
                            </span>
                        );
                    }
                    return '-';
                },
            },
            {
                label: t('driver.pendingTransaction.label.createdDate'),
                id: 'created_date',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverPendingTransactions) => {
                    if (row.created_date) {
                        return moment(row.created_date).format(regionDateAndTime().GENERAL_DATE_TIME_FORMAT);
                    }
                    return '-';
                },
            },
        ];

        return (
            <Modal
                className={`set-pending-transactions-modal-width ${
                    this.state.modal.showModal ? 'modal-fade-background' : ''
                }`}
                show={showModal}
                onHide={closeModal}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('driver.label.cancelPendingTransactions')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ maxHeight: '620px', overflow: 'auto' }}>
                        <Table
                            columns={header}
                            data={data}
                            rowsPerPage={filterState.rowsPerPage}
                            currentPage={filterState.currentPage}
                            totalCount={totalCount}
                            sortColumn={filterState.sortColumn}
                            sortOrder={filterState.sortOrder}
                            handlePageChange={this.handlePageChange}
                            handleSorting={this.handleSorting}
                            isCheckboxGrid={true}
                            checkedRecord={checkedRecord}
                            setCheckedRecord={(data: number[]) => this.setState({ checkedRecord: data })}
                            isCheckboxShow={(row: IDriverPendingTransactions) => {
                                if (
                                    (row.session_status?.code !== Common.SESSION_STATUS.PENDING &&
                                        row.session_status?.code !== Common.SESSION_STATUS.ACTIVE) ||
                                    row.session_status == null
                                ) {
                                    return true;
                                }
                                return false;
                            }}
                            t={t}
                        />

                        <ConfirmBox
                            t={this.props.t}
                            closeModel={this.handleModal}
                            header={this.state.modal.header}
                            body={this.state.modal.body}
                            callBack={this.state.modal.callBack}
                            showModel={this.state.modal.showModal}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        className="btn btn-primary"
                        onClick={() =>
                            this.handleModal(
                                t('driver.message.cancelPendingTransactionsConfirmMesage'),
                                t('driver.label.cancelTransactions'),
                                this.settlePendingTransactions,
                            )
                        }
                        title={t('driver.pendingTransaction.button.settle')}
                        disabled={checkedRecord.length === 0}
                    >
                        <span>{t('driver.pendingTransaction.button.settle')}</span>
                    </Button>
                    <Button
                        className="btn btn-secondary"
                        onClick={closeModal}
                        title={t('driver.pendingTransaction.button.cancel')}
                    >
                        <span>{t('driver.pendingTransaction.button.cancel')}</span>
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default CancelPendingTransactionsModal;
